// src/components/shared/forms/EntityTeamForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Box,
  Chip,
  useTheme,
  alpha,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as AddPersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  SupervisorAccount as ManagerIcon,
  Create as CreatorIcon,
  Visibility as ObserverIcon,
  Analytics as AnalystIcon,
  HowToReg as ApproverIcon,
  Settings as AdminIcon,
  Business as ClientIcon,
  LocalAtm as FinanceIcon,
  Info as InfoIcon
} from '@mui/icons-material';

/**
 * Standardized entity team form component for managing team members across different entities
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - The entity data object
 * @param {Function} props.onChange - Function to handle changes to the team array
 * @param {Array} props.availableUsers - List of available users to add
 * @param {Boolean} props.isLoading - Loading state flag
 * @param {String} props.entityType - Type of entity (e.g., 'campaign', 'project', 'brand', etc.)
 * @param {String} props.teamField - Name of the field in the entity data that contains the team members (default: 'team')
 * @param {Array} props.roleOptions - Available role options
 * @param {Object} props.defaultPermissions - Default permissions for new team members
 * @param {Function} props.fetchUsers - Optional function to fetch users if not provided through availableUsers
 * @param {Object} props.userRoleMapping - Mapping from system user roles to entity roles for suggestions
 * @param {Function} props.renderRoleIcon - Function to render role icon
 * @param {Object} props.errors - Validation errors object
 * @param {Object} props.touched - Form touched fields object
 * @param {String} props.title - Form section title
 * @param {String} props.description - Form section description
 * @returns {React.ReactElement}
 */
const EntityTeamForm = ({
  data,
  onChange,
  availableUsers = [],
  isLoading = false,
  entityType = 'entity',
  teamField = 'team',
  roleOptions = DEFAULT_ROLE_OPTIONS,
  defaultPermissions = DEFAULT_PERMISSIONS,
  fetchUsers = null,
  userRoleMapping = DEFAULT_USER_ROLE_MAPPING,
  renderRoleIcon = null,
  errors = {},
  touched = {},
  title = 'Team Members',
  description = 'Assign team members with specific roles and permissions.',
  icon = <PersonIcon />
}) => {
  const theme = useTheme();
  const [teamMembers, setTeamMembers] = useState(data[teamField] || []);
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [memberForm, setMemberForm] = useState({...initialMemberForm});
  const [users, setUsers] = useState(availableUsers);
  const [selectedUserRole, setSelectedUserRole] = useState('');
  const [localIsLoading, setLocalIsLoading] = useState(false);

  // Default role icon renderer
  const defaultRenderRoleIcon = (role) => {
    const roleOption = roleOptions.find(option => option.value === role);
    return roleOption?.icon || <PersonIcon />;
  };

  // Use provided renderRoleIcon or default implementation
  const getRoleIcon = renderRoleIcon || defaultRenderRoleIcon;

  // Initialize teamMembers state when data[teamField] changes
  useEffect(() => {
    if (data && data[teamField] && JSON.stringify(data[teamField]) !== JSON.stringify(teamMembers)) {
      setTeamMembers(data[teamField] || []);
    }
  }, [data, teamField]);

  // Load users on component mount if fetchUsers provided and availableUsers not
  useEffect(() => {
    const loadUsers = async () => {
      if (fetchUsers && availableUsers.length === 0) {
        setLocalIsLoading(true);
        try {
          const fetchedUsers = await fetchUsers();
          setUsers(fetchedUsers);
        } catch (error) {
          console.error(`Failed to load users for ${entityType}:`, error);
        } finally {
          setLocalIsLoading(false);
        }
      } else {
        setUsers(availableUsers);
      }
    };

    loadUsers();
  }, [fetchUsers, availableUsers, entityType]);

  // Initial member form state
  const initialMemberForm = {
    user: '',
    role: roleOptions[0]?.value || 'observer',
    permissions: { ...defaultPermissions }
  };

  // Handle team members changes and notify parent component
  const handleTeamMembersChange = (newTeamMembers) => {
    setTeamMembers(newTeamMembers);
    // Notify parent component of the change
    onChange(teamField, newTeamMembers);
  };

  // Open dialog to add new member
  const handleAddMember = () => {
    setMemberForm({...initialMemberForm});
    setSelectedUserRole('');
    setEditIndex(null);
    setOpenDialog(true);
  };

  // Open dialog to edit member
  const handleEditMember = (index) => {
    const member = teamMembers[index];
    setMemberForm({
      user: member.user._id || member.user,
      role: member.role,
      permissions: { ...member.permissions }
    });
    
    // Set the selected user role if user details are available
    if (member.user && member.user.role) {
      setSelectedUserRole(member.user.role);
    } else {
      setSelectedUserRole('');
    }
    
    setEditIndex(index);
    setOpenDialog(true);
  };

  // Delete team member
  const handleDeleteMember = (index) => {
    const updatedMembers = [...teamMembers];
    updatedMembers.splice(index, 1);
    handleTeamMembersChange(updatedMembers);
  };

  // Close member dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMemberForm({...initialMemberForm});
    setSelectedUserRole('');
    setEditIndex(null);
  };

  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value, checked } = e.target;
    if (name.startsWith('permissions.')) {
      const permissionName = name.split('.')[1];
      setMemberForm(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionName]: checked
        }
      }));
    } else {
      setMemberForm(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // If user changes, set the user role and suggest permissions
    if (name === 'user') {
      const selectedUser = users.find(user => user._id === value);
      if (selectedUser) {
        setSelectedUserRole(selectedUser.role);
        
        // Suggest a role based on user role
        const userRole = selectedUser.role;
        const suggestedRoles = userRoleMapping[userRole] || ['observer'];
        setMemberForm(prev => ({
          ...prev,
          role: suggestedRoles[0] || 'observer'
        }));
        
        // Auto-assign permissions based on suggested role
        handleRolePermissions(suggestedRoles[0]);
      }
    }

    // Auto-assign permissions based on role
    if (name === 'role') {
      handleRolePermissions(value);
    }
  };
  
  // Handle role-based permissions
  const handleRolePermissions = (role) => {
    // Find the role option
    const roleOption = roleOptions.find(option => option.value === role);
    
    // If role has default permissions, use them
    if (roleOption && roleOption.defaultPermissions) {
      setMemberForm(prev => ({
        ...prev,
        permissions: {
          ...defaultPermissions,
          ...roleOption.defaultPermissions
        }
      }));
    } else {
      // Apply default logic
      let permissions = { ...defaultPermissions };
      
      // Common pattern for roles
      switch(role) {
        case 'manager':
        case 'project_manager':
        case 'team_lead':
          permissions = { 
            ...permissions,
            canEdit: true, 
            canApprove: true, 
            canManageBudget: true,
            canManageTasks: true 
          };
          break;
        case 'creator':
        case 'content_creator':
        case 'team_member':
          permissions = { 
            ...permissions,
            canEdit: true, 
            canManageTasks: true 
          };
          break;
        case 'approver':
        case 'content_approver':
          permissions = { 
            ...permissions,
            canApprove: true 
          };
          break;
        case 'analyst':
        case 'finance_manager':
          permissions = { 
            ...permissions, 
            canManageBudget: true 
          };
          break;
        case 'client':
        case 'stakeholder':
        case 'observer':
        default:
          // Basic permissions
          break;
      }
      
      setMemberForm(prev => ({
        ...prev,
        permissions
      }));
    }
  };

  // Save team member
  const handleSaveMember = () => {
    // Validate required fields
    if (!memberForm.user || !memberForm.role) {
      return;
    }

    // Find selected user
    const selectedUser = users.find(user => user._id === memberForm.user);
    if (!selectedUser) {
      return;
    }

    const newMember = {
      user: selectedUser,
      role: memberForm.role,
      permissions: memberForm.permissions
    };

    let updatedMembers;
    if (editIndex !== null) {
      // Update existing member
      updatedMembers = [...teamMembers];
      updatedMembers[editIndex] = newMember;
    } else {
      // Add new member
      updatedMembers = [...teamMembers, newMember];
    }

    handleTeamMembersChange(updatedMembers);
    handleCloseDialog();
  };

  // Get appropriate roles for a user based on their system role
  const getAppropriateRoles = (userRole) => {
    return userRoleMapping[userRole] || ['observer'];
  };

  // User role badge component
  const UserRoleBadge = ({ role }) => {
    let color;
    switch(role) {
      case 'superadmin':
      case 'admin':
        color = 'error';
        break;
      case 'agency-admin':
      case 'agency-manager':
        color = 'primary';
        break;
      case 'client-admin':
      case 'client-manager':
        color = 'secondary';
        break;
      case 'content-creator':
      case 'content-approver':
        color = 'info';
        break;
      case 'finance-manager':
        color = 'warning';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={role} 
        size="small" 
        color={color}
        sx={{ ml: 1, fontSize: '0.7rem' }}
      />
    );
  };

  // Get user role icon
  const getUserRoleIcon = (role) => {
    switch(role) {
      case 'superadmin':
      case 'admin':
        return <AdminIcon />;
      case 'agency-admin':
      case 'agency-manager':
        return <ManagerIcon />;
      case 'client-admin':
      case 'client-manager':
        return <ClientIcon />;
      case 'content-creator':
        return <CreatorIcon />;
      case 'content-approver':
        return <ApproverIcon />;
      case 'finance-manager':
        return <FinanceIcon />;
      default:
        return <PersonIcon />;
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon && <Box sx={{ mr: 1, color: 'primary.main' }}>{icon}</Box>}
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" paragraph>
          {description}
        </Typography>
      </Grid>

      {/* Team Members List */}
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardHeader 
            title="Team Members" 
            action={
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddPersonIcon />}
                onClick={handleAddMember}
              >
                Add Team Member
              </Button>
            }
          />
          <Divider />
          <CardContent>
            {teamMembers.length === 0 ? (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <Typography color="textSecondary" gutterBottom>
                  No team members assigned yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Add team members to collaborate on this {entityType}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddPersonIcon />}
                  onClick={handleAddMember}
                  sx={{ mt: 2 }}
                >
                  Add Team Member
                </Button>
              </Box>
            ) : (
              <List>
                {teamMembers.map((member, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem
                      secondaryAction={
                        <Box>
                          <IconButton edge="end" onClick={() => handleEditMember(index)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton edge="end" onClick={() => handleDeleteMember(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar 
                          src={member.user.photo}
                          alt={member.user.firstName}
                          sx={{ 
                            bgcolor: theme => 
                              member.role === 'manager' || member.role === 'project_manager' ? theme.palette.primary.main :
                              member.role === 'approver' || member.role === 'content_approver' ? theme.palette.success.main :
                              member.role === 'creator' || member.role === 'content_creator' ? theme.palette.info.main :
                              member.role === 'analyst' || member.role === 'finance_manager' ? theme.palette.warning.main : 
                              theme.palette.grey[500]
                          }}
                        >
                          {getRoleIcon(member.role)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography>{member.user.firstName} {member.user.lastName}</Typography>
                            <Tooltip title="System Role">
                              <UserRoleBadge role={member.user.role} />
                            </Tooltip>
                            <Tooltip title={`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Role`}>
                              <Chip 
                                label={roleOptions.find(r => r.value === member.role)?.label || member.role} 
                                size="small"
                                color={
                                  member.role === 'manager' || member.role === 'project_manager' ? 'primary' :
                                  member.role === 'approver' || member.role === 'content_approver' ? 'success' :
                                  member.role === 'creator' || member.role === 'content_creator' ? 'info' :
                                  member.role === 'analyst' || member.role === 'finance_manager' ? 'warning' : 'default'
                                }
                              />
                            </Tooltip>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            {member.permissions.canEdit && (
                              <Chip 
                                label="Can Edit" 
                                size="small" 
                                variant="outlined" 
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {member.permissions.canApprove && (
                              <Chip 
                                label="Can Approve" 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {member.permissions.canManageBudget && (
                              <Chip 
                                label="Can Manage Budget" 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {member.permissions.canManageTasks && (
                              <Chip 
                                label="Can Manage Tasks" 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {member.permissions.canComment && (
                              <Chip 
                                label="Can Comment" 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Role Descriptions */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          {entityType.charAt(0).toUpperCase() + entityType.slice(1)} Role Descriptions
        </Typography>
        <Grid container spacing={2}>
          {roleOptions.map((role) => (
            <Grid item xs={12} sm={6} md={4} key={role.value}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar 
                      sx={{ 
                        mr: 1, 
                        bgcolor: theme => 
                          role.value === 'manager' || role.value === 'project_manager' ? theme.palette.primary.main :
                          role.value === 'approver' || role.value === 'content_approver' ? theme.palette.success.main :
                          role.value === 'creator' || role.value === 'content_creator' ? theme.palette.info.main :
                          role.value === 'analyst' || role.value === 'finance_manager' ? theme.palette.warning.main : 
                          theme.palette.grey[500]
                      }}
                    >
                      {role.icon}
                    </Avatar>
                    <Typography variant="subtitle1">
                      {role.label}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {role.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Team Member Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editIndex !== null ? "Edit Team Member" : "Add Team Member"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* User Select */}
            <Grid item xs={12}>
              <FormControl fullWidth disabled={localIsLoading || isLoading}>
                <InputLabel id="user-label">Team Member</InputLabel>
                <Select
                  labelId="user-label"
                  id="user"
                  name="user"
                  value={memberForm.user}
                  onChange={handleFormChange}
                  label="Team Member"
                  required
                >
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar 
                          src={user.photo} 
                          sx={{ width: 24, height: 24, mr: 1 }}
                        >
                          {getUserRoleIcon(user.role)}
                        </Avatar>
                        <Typography>{user.firstName} {user.lastName}</Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary" 
                          sx={{ ml: 'auto' }}
                        >
                          {user.role}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Selected User System Role */}
            {selectedUserRole && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                    System Role:
                  </Typography>
                  <UserRoleBadge role={selectedUserRole} />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    ml: { xs: 0, sm: 'auto' }, 
                    mt: { xs: 1, sm: 0 },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Suggested Roles:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {getAppropriateRoles(selectedUserRole).map(role => (
                        <Chip 
                          key={role} 
                          label={roleOptions.find(r => r.value === role)?.label || role} 
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Role Select */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="role-label">{entityType.charAt(0).toUpperCase() + entityType.slice(1)} Role</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={memberForm.role}
                  onChange={handleFormChange}
                  label={`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Role`}
                  required
                >
                  {roleOptions.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      disabled={selectedUserRole && !getAppropriateRoles(selectedUserRole).includes(option.value)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>{option.icon}</Box>
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Permissions */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {entityType.charAt(0).toUpperCase() + entityType.slice(1)} Permissions
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions?.canEdit || false}
                    onChange={handleFormChange}
                    name="permissions.canEdit"
                  />
                }
                label={`Can edit ${entityType} content`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions?.canApprove || false}
                    onChange={handleFormChange}
                    name="permissions.canApprove"
                  />
                }
                label={`Can approve ${entityType} content`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions?.canManageBudget || false}
                    onChange={handleFormChange}
                    name="permissions.canManageBudget"
                  />
                }
                label={`Can manage ${entityType} budget`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions?.canManageTasks || false}
                    onChange={handleFormChange}
                    name="permissions.canManageTasks"
                  />
                }
                label={`Can manage ${entityType} tasks`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions?.canComment || false}
                    onChange={handleFormChange}
                    name="permissions.canComment"
                  />
                }
                label="Can add comments"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveMember} 
            variant="contained" 
            color="primary"
            startIcon={<CheckIcon />}
            disabled={!memberForm.user || !memberForm.role || isLoading || localIsLoading}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Management Tips */}
      <Grid item xs={12}>
        <Box sx={{ 
          p: 2, 
          bgcolor: alpha(theme.palette.info.main, 0.1), 
          borderRadius: 1,
          mt: 3,
          display: 'flex',
          alignItems: 'flex-start'
        }}>
          <InfoIcon sx={{ mt: 0.5, mr: 1, color: 'info.main' }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Team Management Tips:
            </Typography>
            <Typography variant="body2">
              • System roles determine what a user can access in the platform
            </Typography>
            <Typography variant="body2">
              • {entityType.charAt(0).toUpperCase() + entityType.slice(1)} roles define responsibilities specific to this {entityType}
            </Typography>
            <Typography variant="body2">
              • Assign at least one Manager for oversight
            </Typography>
            <Typography variant="body2">
              • Consider having separate Creators and Approvers for better quality control
            </Typography>
            <Typography variant="body2">
              • Limit budget management permissions to appropriate team members
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

// Default role options
const DEFAULT_ROLE_OPTIONS = [
  { 
    value: 'manager', 
    label: 'Manager', 
    icon: <ManagerIcon />, 
    description: 'Oversees the entire entity with full permissions',
    defaultPermissions: {
      canEdit: true,
      canApprove: true,
      canManageBudget: true,
      canManageTasks: true,
      canComment: true
    }
  },
  { 
    value: 'creator', 
    label: 'Content Creator', 
    icon: <CreatorIcon />, 
    description: 'Creates content for the entity',
    defaultPermissions: {
      canEdit: true,
      canApprove: false,
      canManageBudget: false,
      canManageTasks: true,
      canComment: true
    }
  },
  { 
    value: 'approver', 
    label: 'Content Approver', 
    icon: <ApproverIcon />, 
    description: 'Reviews and approves entity content',
    defaultPermissions: {
      canEdit: false,
      canApprove: true,
      canManageBudget: false,
      canManageTasks: false,
      canComment: true
    }
  },
  { 
    value: 'analyst', 
    label: 'Analyst', 
    icon: <AnalystIcon />, 
    description: 'Monitors entity performance and metrics',
    defaultPermissions: {
      canEdit: false,
      canApprove: false,
      canManageBudget: true,
      canManageTasks: false,
      canComment: true
    }
  },
  { 
    value: 'observer', 
    label: 'Observer', 
    icon: <ObserverIcon />, 
    description: 'Has view-only access to the entity',
    defaultPermissions: {
      canEdit: false,
      canApprove: false,
      canManageBudget: false,
      canManageTasks: false,
      canComment: true
    }
  },
];

// Default permissions for new team members
const DEFAULT_PERMISSIONS = {
  canEdit: false,
  canApprove: false,
  canManageBudget: false,
  canManageTasks: false,
  canComment: true
};

// Default user role mapping to entity roles
const DEFAULT_USER_ROLE_MAPPING = {
  'superadmin': ['manager', 'approver', 'analyst', 'creator', 'observer'],
  'admin': ['manager', 'approver', 'analyst', 'creator', 'observer'],
  'agency-admin': ['manager', 'approver', 'analyst', 'creator', 'observer'],
  'agency-manager': ['manager', 'approver', 'analyst', 'creator', 'observer'],
  'content-creator': ['creator'],
  'content-approver': ['approver'],
  'finance-manager': ['analyst'],
  'client-admin': ['manager', 'approver', 'analyst'],
  'client-manager': ['manager', 'approver'],
  'client-user': ['observer'],
  'user': ['observer']
};

export default EntityTeamForm;